// Answer control for "fraction entry" questions (2026-10-13): one fraction
// or mixed number, typed as a whole number (if there is one), a top number
// and a bottom number. The whole-number box is always there, so it gives
// nothing away, and may be left empty. Like BoxesAnswer.tsx, the boxes are
// controlled by the practice screen's own answer text (three lines, see
// fractionAnswerText.ts), so the screen's existing reset-per-question and
// retry-with-the-same-key behaviour applies to them unchanged. They send
// {responseKind: 'ordered_set', value: {order: ["f=1 4/10"]}}.
//
// Questions that can have a negative answer (content `negatives: true`, Year
// 7 on, 2 Nov 2026) also get a minus-sign button in front of the fraction.
// It is on every such question, so it gives nothing away either. Tapped, it
// shows a big "−" and is read out as "Minus sign, pressed"; tapped again,
// the answer is positive. It sends "f=-3/4".

import { FractionStack } from '../../components/MathText';
import {
  FRACTION_MAX_LENGTH,
  FRACTION_PART_NAMES,
  FRACTION_PARTS,
  fractionBottomIsZero,
  fractionDigits,
  joinFractionParts,
  setFractionNegative,
  splitFractionParts,
  type FractionPart,
} from './fractionAnswerText';

type FractionProps = {
  value: string;
  // Whether the minus-sign button is shown.
  negatives?: boolean;
  // The box the number pad types into: the one last tapped or tabbed to, or
  // the numerator to begin with (most answers have no whole number). It is
  // shown shaded, but no box is focused until the student taps or tabs into
  // one, so a phone's own keyboard does not open over the number pad.
  activeId: FractionPart;
  disabled: boolean;
  onChange: (value: string) => void;
  onActive: (id: FractionPart) => void;
  // Enter in the bottom box.
  onSubmit: () => void;
};

const NOTE_ID = 'practice-fraction-note';

function partInputId(id: FractionPart): string {
  return `practice-fraction-${id}`;
}

// A real, focusable, keyboard/screen-reader-operable input, same as the
// single number box. Room for three digits from the start, so the fraction
// stays put as they are typed.
function PartInput({
  part,
  value,
  activeId,
  disabled,
  onChange,
  onActive,
  onSubmit,
}: FractionProps & { part: FractionPart }) {
  const parts = splitFractionParts(value);
  const bottomIsZero = part === 'd' && fractionBottomIsZero(value);
  return (
    <input
      id={partInputId(part)}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      aria-label={FRACTION_PART_NAMES[part]}
      aria-invalid={part === 'd' ? bottomIsZero : undefined}
      aria-describedby={part === 'd' ? NOTE_ID : undefined}
      value={parts[part]}
      maxLength={FRACTION_MAX_LENGTH}
      disabled={disabled}
      className={`block h-12 w-[calc(3ch+1.5rem)] rounded-md border-2 border-dashed border-forest-600 px-1 text-center text-forest-800 focus-visible:border-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-600 ${
        activeId === part ? 'bg-sage' : 'bg-white'
      }`}
      onFocus={() => {
        onActive(part);
      }}
      onChange={(event) => {
        onChange(
          joinFractionParts({
            ...parts,
            [part]: fractionDigits(event.target.value),
          }),
        );
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        // Enter moves on to the next box; in the bottom box it submits.
        const next = FRACTION_PARTS[FRACTION_PARTS.indexOf(part) + 1];
        if (next) document.getElementById(partInputId(next))?.focus();
        else onSubmit();
      }}
    />
  );
}

// The minus-sign button: as tall as a box and as wide as it is tall, so it
// is easy to tap. Off, it shows "+/−" in quiet grey, dashed like an empty
// box; on, a big dark "−", solid and shaded.
function SignToggle({
  value,
  disabled,
  onChange,
}: Pick<FractionProps, 'value' | 'disabled' | 'onChange'>) {
  const negative = splitFractionParts(value).negative === true;
  return (
    <button
      type="button"
      aria-label="Minus sign"
      aria-pressed={negative}
      disabled={disabled}
      className={`mr-2 flex h-12 min-w-12 items-center justify-center rounded-md border-2 px-1 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-600 ${
        negative
          ? 'border-solid border-forest-600 bg-sage text-4xl text-forest-800'
          : 'border-dashed border-forest-600 bg-white text-base text-muted'
      }`}
      onClick={() => {
        onChange(setFractionNegative(value, !negative));
      }}
    >
      <span aria-hidden="true">{negative ? '−' : '+/−'}</span>
    </button>
  );
}

export function FractionAnswer(props: FractionProps) {
  return (
    <div
      role="group"
      aria-label="Your answer"
      className="grid justify-items-center gap-2"
    >
      <div className="flex items-center justify-center text-3xl font-semibold">
        {props.negatives === true && (
          <SignToggle
            value={props.value}
            disabled={props.disabled}
            onChange={props.onChange}
          />
        )}
        <FractionStack
          whole={<PartInput {...props} part="w" />}
          numerator={<PartInput {...props} part="n" />}
          denominator={<PartInput {...props} part="d" />}
          label="answer"
          roomy
        />
      </div>
      {/* Always here, and a line tall, so the message is read out when it
          appears and nothing below it moves. */}
      <p
        id={NOTE_ID}
        role="status"
        className="min-h-6 text-center text-base text-muted"
      >
        {fractionBottomIsZero(props.value)
          ? 'The denominator cannot be 0.'
          : ''}
      </p>
    </div>
  );
}
